/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../AuthProvider';
import { UsersContext } from '../UsersProvider';
import { LanguageCode } from '../../juristec-ui/utils/language/availableLanguages';

interface LanguageState {
  isLoading: boolean;
  language: LanguageCode;
}

const initialState: LanguageState = {
  isLoading: false,
  language: 'pt',
};

interface LanguageAction {
  type: 'setLanguage' | 'setLoading';
  language?: LanguageCode;
  isLoading?: boolean;
}

interface SetLanguageAction extends LanguageAction {
  type: 'setLanguage';
  language: LanguageCode;
}

interface SetLoadingAction extends LanguageAction {
  type: 'setLoading';
  isLoading: boolean;
}

interface ChangeLanguageAction {
  type: 'changeLanguage';
  language: LanguageCode;
}

interface InitAction {
  type: 'init';
}

const reduce = (state: LanguageState, action: SetLanguageAction | SetLoadingAction) => {
  switch (action.type) {
    case 'setLanguage': {
      return {
        ...state,
        language: action.language,
        isLoading: false,
      };
    }
    case 'setLoading': {
      return {
        ...state,
        isLoading: action.isLoading,
      };
    }
    default:
      return state;
  }
};

interface LanguageContextValue {
  state: LanguageState;
  languageAPI: {
    init: () => void;
    changeLanguage: (language: LanguageCode) => void;
  };
}

export const LanguageContext = createContext<LanguageContextValue>({
  state: initialState,
  languageAPI: {
    init: () => { },
    changeLanguage: () => { },
  },
});

interface LanguageProviderProps {
  children: ReactNode;
}

const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { i18n } = useTranslation();
  const { currentUser, user, changeUserConfigs } = useContext(AuthContext);
  const { usersAPI } = useContext<any>(UsersContext);

  const [state, dispatch] = useReducer(reduce, initialState);
  const setLoading = useCallback((l = true) => dispatch({ type: 'setLoading', isLoading: l }), []);

  const init = useCallback(() => {
    const language = (user.configs && user.configs.language) || i18n.language?.split('-')[0] || 'pt';
    i18n.changeLanguage(language);
    return language;
  }, [currentUser, user, i18n]);

  const changeLanguage = useCallback(async (language: LanguageCode) => {
    /* Saves in localStorage */
    changeUserConfigs({ language });
    /* Saves in firestore */
    const res = await usersAPI.setUserConfigs({ language });
    if (res.error) console.log(res);
    /* Changes language in i18n */
    i18n.changeLanguage(language);
    return language;
  }, [currentUser, user, i18n]);

  const middleware = useCallback(async (action: ChangeLanguageAction | InitAction) => {
    switch (action.type) {
      case 'init': {
        setLoading(true);
        dispatch({
          type: 'setLanguage',
          language: init(),
        });
        break;
      }
      case 'changeLanguage': {
        setLoading(true);
        dispatch({
          type: 'setLanguage',
          language: await changeLanguage(action.language),
        });
        break;
      }
      default: {
        break;
      }
    }
  }, [
    init,
    changeLanguage,
  ]);

  const languageAPI = useMemo(() => ({
    init: () => middleware({ type: 'init' }),
    changeLanguage: (language: LanguageCode) => middleware({
      type: 'changeLanguage', language,
    }),
  }), [middleware]);

  return (
    <LanguageContext.Provider value={{ state, languageAPI }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
